import React, { Component } from 'react';
import { mmToPx, getShadowCss, isClipShape } from './templateUtils';

const HANDLE_SIZE = 8; // px in inner (pre-transform) canvas space
const MIN_SIZE_MM = 5;
const MM_TO_PX_96DPI = 96 / 25.4;

const HANDLES = [
    { name: 'nw', style: { top: -HANDLE_SIZE / 2, left:  -HANDLE_SIZE / 2 },                                   cursor: 'nw-resize' },
    { name: 'n',  style: { top: -HANDLE_SIZE / 2, left: `calc(50% - ${HANDLE_SIZE / 2}px)` },                  cursor: 'n-resize'  },
    { name: 'ne', style: { top: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 },                                   cursor: 'ne-resize' },
    { name: 'w',  style: { top: `calc(50% - ${HANDLE_SIZE / 2}px)`, left:  -HANDLE_SIZE / 2 },                 cursor: 'w-resize'  },
    { name: 'e',  style: { top: `calc(50% - ${HANDLE_SIZE / 2}px)`, right: -HANDLE_SIZE / 2 },                 cursor: 'e-resize'  },
    { name: 'sw', style: { bottom: -HANDLE_SIZE / 2, left:  -HANDLE_SIZE / 2 },                                cursor: 'sw-resize' },
    { name: 's',  style: { bottom: -HANDLE_SIZE / 2, left: `calc(50% - ${HANDLE_SIZE / 2}px)` },               cursor: 's-resize'  },
    { name: 'se', style: { bottom: -HANDLE_SIZE / 2, right: -HANDLE_SIZE / 2 },                                cursor: 'se-resize' },
];

class CanvasElement extends Component {
    constructor(props) {
        super(props);
        this._containerRef = React.createRef();
        this._dragStartX = 0;
        this._dragStartY = 0;
        this._elStartX = 0;
        this._elStartY = 0;
        this._dragging = false;

        this._resizing = false;
        this._resizeHandle = null;
        this._resizeStartX = 0;
        this._resizeStartY = 0;
        this._elStartW = 0;
        this._elStartH = 0;

        this._rotating = false;
        this._rotateCenterX = 0;
        this._rotateCenterY = 0;
        this._rotateStartAngle = 0;
        this._rotateStartRotation = 0;

        this._radiusDragging = false;
        this._radiusStartX = 0;
        this._radiusStartY = 0;
        this._radiusStart = 0;
    }

    componentWillUnmount() {
        this._removeListeners();
    }

    _removeListeners = () => {
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mouseup', this._onMouseUp);
        window.removeEventListener('mousemove', this._onResizeMouseMove);
        window.removeEventListener('mouseup', this._onResizeMouseUp);
        window.removeEventListener('mousemove', this._onRotateMouseMove);
        window.removeEventListener('mouseup', this._onRotateMouseUp);
        window.removeEventListener('mousemove', this._onRadiusMouseMove);
        window.removeEventListener('mouseup', this._onRadiusMouseUp);
    };

    // ─── Move ──────────────────────────────────────────────────────────────────

    _onHandleMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const { element, selected } = this.props;
        const additive = e.ctrlKey || e.metaKey || e.shiftKey;

        // modifier-click toggles the multi-selection (no drag); locked is selectable only;
        // plain drag on a selected member keeps the current multi-selection
        if (additive) {
            this.props.onSelect(element.id, true);
            return;
        }
        if (element.locked) {
            this.props.onSelect(element.id);
            return;
        }
        if (!selected) this.props.onSelect(element.id);

        this._dragging = true;
        this._didDrag = false;
        this._dragStartX = e.clientX;
        this._dragStartY = e.clientY;
        this._elStartX = element.x;
        this._elStartY = element.y;

        window.addEventListener('mousemove', this._onMouseMove);
        window.addEventListener('mouseup', this._onMouseUp);
    };

    _onMouseMove = (e) => {
        if (!this._dragging) return;
        this._didDrag = true;

        const { zoom, onMove, element, canvasWMm, canvasHMm } = this.props;

        const deltaMmX = Math.round((e.clientX - this._dragStartX) / (MM_TO_PX_96DPI * zoom) * 10) / 10;
        const deltaMmY = Math.round((e.clientY - this._dragStartY) / (MM_TO_PX_96DPI * zoom) * 10) / 10;

        const newX = Math.max(0, Math.min(this._elStartX + deltaMmX, canvasWMm - element.w));
        const newY = Math.max(0, Math.min(this._elStartY + deltaMmY, canvasHMm - element.h));

        onMove(element.id, newX, newY);
    };

    _onMouseUp = () => {
        this._dragging = false;
        window.removeEventListener('mousemove', this._onMouseMove);
        window.removeEventListener('mouseup', this._onMouseUp);
    };

    // ─── Resize ────────────────────────────────────────────────────────────────

    _onResizeHandleMouseDown = (handleName, e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const { element } = this.props;
        this._resizing = true;
        this._resizeHandle = handleName;
        this._resizeStartX = e.clientX;
        this._resizeStartY = e.clientY;
        this._elStartX = element.x;
        this._elStartY = element.y;
        this._elStartW = element.w;
        this._elStartH = element.h;

        window.addEventListener('mousemove', this._onResizeMouseMove);
        window.addEventListener('mouseup', this._onResizeMouseUp);
    };

    _onResizeMouseMove = (e) => {
        if (!this._resizing) return;

        const { zoom, onResize, element, canvasWMm, canvasHMm } = this.props;
        if (!onResize) return;

        let rawDx = (e.clientX - this._resizeStartX) / (MM_TO_PX_96DPI * zoom);
        let rawDy = (e.clientY - this._resizeStartY) / (MM_TO_PX_96DPI * zoom);

        // project the screen delta onto the element's local axes so resize handles on a
        // rotated element stretch their own edge
        const rot = (element.rotation || 0) * (Math.PI / 180);
        if (rot !== 0) {
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);
            const localDx = rawDx * cos + rawDy * sin;
            const localDy = -rawDx * sin + rawDy * cos;
            rawDx = localDx;
            rawDy = localDy;
        }

        const { _elStartX: x0, _elStartY: y0, _elStartW: w0, _elStartH: h0, _resizeHandle: handle } = this;

        let newX = x0, newY = y0, newW = w0, newH = h0;

        // Width / x
        if (handle.includes('e')) {
            newW = Math.max(MIN_SIZE_MM, w0 + rawDx);
        } else if (handle.includes('w')) {
            newW = Math.max(MIN_SIZE_MM, w0 - rawDx);
            newX = x0 + w0 - newW;
        }

        // Height / y
        if (handle.includes('s')) {
            newH = Math.max(MIN_SIZE_MM, h0 + rawDy);
        } else if (handle.includes('n')) {
            newH = Math.max(MIN_SIZE_MM, h0 - rawDy);
            newY = y0 + h0 - newH;
        }

        // Clamp position to canvas bounds
        newX = Math.max(0, Math.min(newX, canvasWMm - newW));
        newY = Math.max(0, Math.min(newY, canvasHMm - newH));
        newW = Math.min(newW, canvasWMm - newX);
        newH = Math.min(newH, canvasHMm - newY);

        onResize(element.id, Math.round(newX * 10) / 10, Math.round(newY * 10) / 10, Math.round(newW * 10) / 10, Math.round(newH * 10) / 10);
    };

    _onResizeMouseUp = () => {
        this._resizing = false;
        this._resizeHandle = null;
        window.removeEventListener('mousemove', this._onResizeMouseMove);
        window.removeEventListener('mouseup', this._onResizeMouseUp);
    };

    // ─── Rotate ────────────────────────────────────────────────────────────────

    _onRotateHandleMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const { element } = this.props;
        const rect = this._containerRef.current ? this._containerRef.current.getBoundingClientRect() : null;
        if (!rect) return;

        // a rotated bbox still shares its centre with the unrotated box
        this._rotateCenterX = rect.left + rect.width / 2;
        this._rotateCenterY = rect.top + rect.height / 2;
        this._rotateStartAngle = Math.atan2(e.clientY - this._rotateCenterY, e.clientX - this._rotateCenterX);
        this._rotateStartRotation = element.rotation || 0;
        this._rotating = true;

        window.addEventListener('mousemove', this._onRotateMouseMove);
        window.addEventListener('mouseup', this._onRotateMouseUp);

        this.props.onSelect(element.id);
    };

    _onRotateMouseMove = (e) => {
        if (!this._rotating) return;

        const { element, onRotate } = this.props;
        if (!onRotate) return;

        const angle = Math.atan2(e.clientY - this._rotateCenterY, e.clientX - this._rotateCenterX);
        let deg = this._rotateStartRotation + (angle - this._rotateStartAngle) * (180 / Math.PI);

        deg = ((deg + 180) % 360 + 360) % 360 - 180;
        if (e.shiftKey) deg = Math.round(deg / 15) * 15; // snap to 15°

        onRotate(element.id, Math.round(deg));
    };

    _onRotateMouseUp = () => {
        this._rotating = false;
        window.removeEventListener('mousemove', this._onRotateMouseMove);
        window.removeEventListener('mouseup', this._onRotateMouseUp);
    };

    // ─── Corner radius (drag the corner knob towards the centre to round) ─────

    _onRadiusHandleMouseDown = (e) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();

        const { element } = this.props;
        this._radiusDragging = true;
        this._radiusStartX = e.clientX;
        this._radiusStartY = e.clientY;
        this._radiusStart = element.borderRadius || 0;

        window.addEventListener('mousemove', this._onRadiusMouseMove);
        window.addEventListener('mouseup', this._onRadiusMouseUp);

        this.props.onSelect(element.id);
    };

    _onRadiusMouseMove = (e) => {
        if (!this._radiusDragging) return;

        const { zoom, element, onRadius } = this.props;
        if (!onRadius) return;

        let dx = (e.clientX - this._radiusStartX) / zoom;
        let dy = (e.clientY - this._radiusStartY) / zoom;

        const rot = (element.rotation || 0) * (Math.PI / 180);
        if (rot !== 0) {
            const cos = Math.cos(rot);
            const sin = Math.sin(rot);
            const localDx = dx * cos + dy * sin;
            const localDy = -dx * sin + dy * cos;
            dx = localDx;
            dy = localDy;
        }

        // dragging diagonally into the element increases the radius (0–50)
        const delta = (dx + dy) / 2;
        const newRadius = Math.max(0, Math.min(50, Math.round(this._radiusStart + delta)));

        onRadius(element.id, newRadius);
    };

    _onRadiusMouseUp = () => {
        this._radiusDragging = false;
        window.removeEventListener('mousemove', this._onRadiusMouseMove);
        window.removeEventListener('mouseup', this._onRadiusMouseUp);
    };

    // ─── Click ─────────────────────────────────────────────────────────────────

    _onClick = (e) => {
        e.stopPropagation();
        // modifier-clicks are handled on mousedown; a click right after a drag must not
        // collapse the multi-selection
        if (e.ctrlKey || e.metaKey || e.shiftKey) return;
        if (this._didDrag) {
            this._didDrag = false;
            return;
        }
        this.props.onSelect(this.props.element.id);
    };

    // ─── Render ────────────────────────────────────────────────────────────────

    render() {
        const { element, zoom, selected, multiSelected, isAiHighlighted } = this.props;
        // per-element handles are hidden in multi-selection (group is move-only)
        const showHandles = selected && !element.locked && !multiSelected;
        // Positions/sizes are in the pre-transform coordinate space (zoom=1).
        // The parent canvas div applies transform:scale(zoom), so we must NOT
        // apply zoom here — doing so would double-scale and make elements appear
        // in the wrong position relative to the page edge.
        const x = mmToPx(element.x, 1);
        const y = mmToPx(element.y, 1);
        const w = mmToPx(element.w, 1);
        const h = mmToPx(element.h, 1);

        const containerStyle = {
            position: 'absolute',
            left: x,
            top: y,
            width: w,
            height: h,
            zIndex: element.zIndex || 1,
            boxSizing: 'border-box',
            // clip shapes shadow via filter; radius here so the shadow hugs the corners
            boxShadow: (!isClipShape(element) && getShadowCss(element.shadow)) || 'none',
            borderRadius: `${element.borderRadius || 0}px`,
            transform: (element.rotation || 0) !== 0 ? `rotate(${element.rotation}deg)` : 'none',
            outline: selected ? '2px solid #007bff' : '1px dashed transparent',
            cursor: 'default',
            userSelect: 'none',
            // locked = click-through so elements behind stay reachable (unlock via layers)
            pointerEvents: element.locked ? 'none' : undefined,
        };

        return (
            <div
                ref={this._containerRef}
                style={containerStyle}
                className={isAiHighlighted ? 'ai-highlighted' : undefined}
                onClick={this._onClick}
            >
                {/* Element content — inset by margin */}
                {(() => {
                    const m = mmToPx(element.margin || 0, 1);
                    return (
                        <div style={{ position: 'absolute', top: m, left: m, right: m, bottom: m, overflow: 'hidden', opacity: (element.opacity ?? 100) / 100 }}>
                            {this.props.children}
                        </div>
                    );
                })()}

                {/* Move handle — entire element surface, below resize handles */}
                <div
                    style={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        cursor: 'move',
                        zIndex: 1,
                    }}
                    onMouseDown={this._onHandleMouseDown}
                />

                {/* Conditional badge — the element only prints when its "Show" condition holds */}
                {element.visibleWhen && (
                    <div
                        title={element.visibleWhen}
                        style={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            zIndex: 3,
                            color: '#fd7e14',
                            fontSize: 11,
                            pointerEvents: 'none',
                        }}
                    >
                        <i className="fa-solid fa-eye-slash" />
                    </div>
                )}

                {/* Lock badge — locked elements are selectable but not editable on canvas */}
                {selected && element.locked && (
                    <div
                        style={{
                            position: 'absolute',
                            top: 2,
                            left: 2,
                            zIndex: 3,
                            color: '#dc3545',
                            fontSize: 11,
                            pointerEvents: 'none',
                        }}
                    >
                        <i className="fa-solid fa-lock" />
                    </div>
                )}

                {/* Rotation handle — stem + knob above the top edge (rotates with the element) */}
                {showHandles && (
                    <>
                        <div
                            style={{
                                position: 'absolute',
                                top: -20,
                                left: 'calc(50% - 1px)',
                                width: 2,
                                height: 20,
                                backgroundColor: '#007bff',
                                zIndex: 2,
                                pointerEvents: 'none',
                            }}
                        />
                        <div
                            title="Shift = 15°"
                            style={{
                                position: 'absolute',
                                top: -32,
                                left: 'calc(50% - 6px)',
                                width: 12,
                                height: 12,
                                borderRadius: '50%',
                                backgroundColor: '#ffffff',
                                border: '1.5px solid #007bff',
                                boxSizing: 'border-box',
                                zIndex: 2,
                                cursor: 'grab',
                            }}
                            onMouseDown={this._onRotateHandleMouseDown}
                        />
                    </>
                )}

                {/* Corner-radius knob — follows the current radius; only for types with radius */}
                {showHandles
                    && (element.type === 'text' || element.type === 'image'
                        || (element.type === 'shape' && (element.shapeType === 'rect' || !element.shapeType)))
                    && (() => {
                        const maxInset = Math.max(6, Math.min(w, h) / 2 - 6);
                        const inset = 8 + Math.min(element.borderRadius || 0, maxInset);
                        return (
                            <div
                                title="Raio do canto"
                                style={{
                                    position: 'absolute',
                                    top: inset - 5,
                                    left: inset - 5,
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    backgroundColor: '#ffffff',
                                    border: '1.5px solid #7b1fa2',
                                    boxSizing: 'border-box',
                                    zIndex: 2,
                                    cursor: 'nwse-resize',
                                }}
                                onMouseDown={this._onRadiusHandleMouseDown}
                            />
                        );
                    })()}

                {/* Resize handles — only when selected and not locked */}
                {showHandles && HANDLES.map(handle => (
                    <div
                        key={handle.name}
                        style={{
                            position: 'absolute',
                            width: HANDLE_SIZE,
                            height: HANDLE_SIZE,
                            backgroundColor: '#ffffff',
                            border: '1.5px solid #007bff',
                            boxSizing: 'border-box',
                            zIndex: 2,
                            cursor: handle.cursor,
                            ...handle.style,
                        }}
                        onMouseDown={(e) => this._onResizeHandleMouseDown(handle.name, e)}
                    />
                ))}
            </div>
        );
    }
}

export default CanvasElement;
